import type {
  DropdownEntry,
  DropdownOption,
  DropdownOptionGroup,
} from "@/components/interactive/DropdownV2.types";
import { createFuse } from "@/utils/fuse";

export const isDropdownOptionGroup = <T extends string | number>(
  entry: DropdownEntry<T>,
): entry is DropdownOptionGroup<T> => {
  return "options" in entry;
};

export const normalizeDropdownQuery = (value: string) => value.trim().toLowerCase();

const optionMatchesQuery = <T extends string | number>(
  option: DropdownOption<T>,
  normalizedQuery: string,
) =>
  option.label.toLowerCase().includes(normalizedQuery) ||
  (option.searchMeta?.some((meta) => meta.toLowerCase().includes(normalizedQuery)) ?? false);

type DropdownSearchCandidate = {
  key: string;
  label: string;
  searchMeta?: readonly string[];
};

const getGroupSearchKey = (entryIndex: number) => `group:${entryIndex}`;
const getOptionSearchKey = (entryIndex: number, optionIndex?: number) =>
  optionIndex == null ? `option:${entryIndex}` : `option:${entryIndex}:${optionIndex}`;

/**
 * Filters and ranks dropdown entries while preserving their group structure.
 * A group-label match keeps the whole group; otherwise only matching children remain.
 */
export const filterDropdownEntries = <T extends string | number>(
  entries: readonly DropdownEntry<T>[],
  query: string,
): DropdownEntry<T>[] => {
  const trimmedQuery = query.trim();
  if (trimmedQuery === "") {
    return [...entries];
  }
  const normalizedQuery = normalizeDropdownQuery(trimmedQuery);

  const candidates = entries.flatMap<DropdownSearchCandidate>((entry, entryIndex) => {
    if (isDropdownOptionGroup(entry)) {
      return [
        { key: getGroupSearchKey(entryIndex), label: entry.label },
        ...entry.options.map((option, optionIndex) => ({
          key: getOptionSearchKey(entryIndex, optionIndex),
          label: option.label,
          searchMeta: option.searchMeta,
        })),
      ];
    }

    return [
      {
        key: getOptionSearchKey(entryIndex),
        label: entry.label,
        searchMeta: entry.searchMeta,
      },
    ];
  });

  // TODO: Expose the ranking strategy when dropdown callers need algorithms other than Fuse.
  const fuse = createFuse(candidates, {
    keys: ["label", "searchMeta"],
    threshold: 0.3,
    findAllMatches: true,
    ignoreLocation: true,
  });
  const resultPriority = new Map(
    fuse.search(trimmedQuery).map((result, index) => [result.item.key, index]),
  );

  const rankedEntries = entries.flatMap<{ entry: DropdownEntry<T>; priority: number }>(
    (entry, entryIndex) => {
      if (!isDropdownOptionGroup(entry)) {
        if (!optionMatchesQuery(entry, normalizedQuery)) {
          return [];
        }
        const priority = resultPriority.get(getOptionSearchKey(entryIndex));
        return [{ entry, priority: priority ?? Number.POSITIVE_INFINITY }];
      }

      const groupPriority = resultPriority.get(getGroupSearchKey(entryIndex));
      const groupMatches = entry.label.toLowerCase().includes(normalizedQuery);
      const rankedOptions = entry.options
        .map((option, optionIndex) => ({
          option,
          matches: optionMatchesQuery(option, normalizedQuery),
          priority:
            resultPriority.get(getOptionSearchKey(entryIndex, optionIndex)) ??
            Number.POSITIVE_INFINITY,
        }))
        .filter(({ matches }) => groupMatches || matches)
        .sort((left, right) => left.priority - right.priority);

      if (rankedOptions.length === 0) {
        return [];
      }

      return [
        {
          entry: { ...entry, options: rankedOptions.map(({ option }) => option) },
          priority: Math.min(groupPriority ?? Number.POSITIVE_INFINITY, rankedOptions[0].priority),
        },
      ];
    },
  );

  return rankedEntries
    .sort((left, right) => left.priority - right.priority)
    .map(({ entry }) => entry);
};

export const groupDropdownSections = <T extends string | number>(
  entries: readonly DropdownEntry<T>[],
): Array<{ title?: string; options: readonly DropdownOption<T>[] }> => {
  const sections: Array<{ title?: string; options: readonly DropdownOption<T>[] }> = [];
  const ungrouped: DropdownOption<T>[] = [];

  entries.forEach((entry) => {
    if (isDropdownOptionGroup(entry)) {
      if (ungrouped.length > 0) {
        sections.push({ options: [...ungrouped] });
        ungrouped.length = 0;
      }

      sections.push({ title: entry.label, options: entry.options });
      return;
    }

    ungrouped.push(entry);
  });

  if (ungrouped.length > 0) {
    sections.push({ options: [...ungrouped] });
  }

  return sections;
};

export const findDropdownOption = <T extends string | number>(
  entries: readonly DropdownEntry<T>[],
  predicate: (option: DropdownOption<T>) => boolean,
): DropdownOption<T> | undefined => {
  const stack = [...entries];
  while (stack.length > 0) {
    const current = stack.pop();
    if (current == null) {
      continue;
    }

    if (isDropdownOptionGroup(current)) {
      stack.push(...current.options);
      continue;
    }

    if (predicate(current)) {
      return current;
    }
  }

  return undefined;
};

export const hasExactDropdownOptionMatch = <T extends string | number>(
  entries: readonly DropdownEntry<T>[],
  normalizedQuery: string,
) =>
  normalizedQuery !== "" &&
  findDropdownOption(
    entries,
    (option) =>
      normalizeDropdownQuery(option.label) === normalizedQuery ||
      normalizeDropdownQuery(String(option.value)) === normalizedQuery,
  ) != null;
